/**
 * The Rekordy tab — KANON `Detal klienta v3`, variant a. A section heading, three big-three cards
 * across the zone, then the PR feed beside the per-exercise table.
 *
 * ── A record is LIME ──────────────────────────────────────────────────────────────────────────
 * Owner, 31 August 2026: everywhere this panel names a record — the star on the activity timeline,
 * the mark on a feed row, the three lift charts — the carrier is `--lime`. That reverses this
 * repo's earlier deviation, which mixed the lime toward the ink for contrast; the reversal is the
 * owner's and is recorded in `AGENTS.md` so the next round does not undo it again. Where the raw
 * hue does not clear 3:1 the fix keeps the hue and moves it, per theme — see `--lime-ink` in
 * `tokens/colors.css` and the palette note in `shared/vendor/vendor.css`.
 *
 * ── The big three are identified by the SERVER, by `Exercise.Code` ────────────────────────────
 * The workspace's rule that business logic never matches by NAME is honoured by not matching at
 * all: the three lifts arrive already identified, always three, null-filled for an untrained one.
 * Nothing on this tab reads an exercise name to decide anything.
 *
 * ── What the canon draws here and this tab does not ───────────────────────────────────────────
 * The `Wybierz ćwiczenie ▾` picker over the per-exercise table. The A5 endpoint answers for ONE
 * exercise per call, so a six-row table behind that control would be a six-request fan-out. The
 * table is folded out of the reads the tab already makes and carries the same numbers, so the
 * picker would add rows nobody is missing. A recorded cut, not an omission.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ReadoutChart } from '~/shared/ui/chart/ReadoutChart';
import { EmptyState } from '~/shared/ui/EmptyState';
import { TrendMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { BarbellIcon, BarsIcon, TrendIcon } from '~/shared/ui/RecordIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { clientRecordsQueryOptions } from '../api/clientDetailQueries';
import { toRecordsSources } from '../api/clientDetailSource';
import { buildRecords, type PrKind } from '../model/records';
import { usePublishTabMeta } from './detailTabMeta';
import { SectionHead } from './SectionHead';
import eyebrows from './cardEyebrow.module.css';
import styles from './DetailTab.module.css';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

interface RecordsTabProps {
  readonly clientId: number;
  readonly reducedMotion: boolean;
}

/**
 * The glyph each record axis wears. One table, so a row cannot end up with one axis's mark and
 * another's word — which is what two parallel lookups drift into.
 *
 * There is no per-axis COLOUR any more: v3 draws every mark on the same lime tile with a dark
 * glyph on it, and the axis is named in words on the row. That is the owner's 31.08 decision about
 * the record colour, and it also fixes the raw-lime-on-white contrast the earlier three-hue tiles
 * were carrying — a dark glyph on lime measures 13.9:1 in both themes.
 */
const AXIS_GLYPH = {
  weight: BarbellIcon,
  e1rm: TrendIcon,
  volume: BarsIcon,
} as const;

export function RecordsTab({ clientId, reducedMotion }: RecordsTabProps) {
  const { t, i18n } = useTranslation();
  const contentLang = useContentLanguage();
  const records = useQuery(clientRecordsQueryOptions(clientId, contentLang));
  const gate = useSkeletonGate(records.isPending);
  const view = buildRecords(records.isSuccess ? toRecordsSources(records.data) : null, {
    t,
    locale: i18n.language,
  });

  usePublishTabMeta(
    records.isSuccess
      ? t('web:clientDetail.tabMeta.records', { count: view.feed.length })
      : null,
  );

  if (records.isError) {
    return (
      <div className={styles.tab}>
        <section className={styles.card}>
          <p className={styles.notice}>{t('web:clientDetail.records.failed')}</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      <SectionHead
        title={t('clients.detail.records.big3')}
        aside={t('web:clientDetail.records.big3Caption')}
        reducedMotion={reducedMotion}
      />

      {/* The row holds its height before the three cards exist.
        *
        * `/records` answers with all three lifts always, null-filled where the client has never
        * done one (`model/records`), so an empty `big3` means the read has not landed — and until
        * it does this grid was ZERO pixels tall, dropping the two cards below it 186px in the frame
        * the response arrived (measured 1 September 2026, f45, 493ms). Reserving the row is what
        * lets the cards fade in without moving anything. */}
      <div className={styles.big3Grid}>
        {view.big3.map((lift) => (
          <section key={lift.key} className={styles.card}>
            <div className={styles.cardHead}>
              <em className={eyebrows.eyebrow}>{lift.title}</em>
              {lift.whenLabel === null ? null : (
                <em className={styles.liftWhen}>{lift.whenLabel}</em>
              )}
            </div>

            {lift.value === null ? (
              <p className={styles.notice}>{t('clients.detail.records.empty')}</p>
            ) : (
              <>
                <p className={styles.liftFigureRow}>
                  <b className={styles.liftFigure}>{lift.value}</b>
                  <em className={styles.liftUnit}>{lift.unit}</em>
                  {lift.aside === null ? null : <em className={styles.liftAside}>{lift.aside}</em>}
                </p>
                {lift.points.length === 0 ? (
                  <em className={styles.caption}>{t('web:clientDetail.records.noPlot')}</em>
                ) : (
                  /* No unit on this plot: the kilograms are in the figure above it, so the gutter
                   * carries bare numbers and the 84px box keeps its width for the line. The axis is
                   * fitted — a lift that went 92,5 → 105 kg on a scale from zero is a flat line. */
                  <ReadoutChart
                    points={lift.points}
                    colour="var(--chart-series-record)"
                    height="var(--h-chart-plot-lift)"
                    name={lift.title}
                    unit={lift.unit}
                    zeroBaseline={false}
                    compact
                    reducedMotion={reducedMotion}
                  />
                )}
              </>
            )}
          </section>
        ))}
      </div>

      <div className={styles.gridSplit}>
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <em className={eyebrows.eyebrow}>{t('web:clientDetail.records.feedEyebrow')}</em>
            <em className={styles.aside}>{t('web:clientDetail.records.feedCaption')}</em>
          </div>

          {/* "Brak rekordów" is an answer about the client, and until `/records` has given one the
            * card draws the shape of the list instead of saying it is reading. Three rows, the same
            * choice and the same reason as Przegląd's activity timeline: the read publishes no
            * count ahead of its rows, this card is the last thing on the tab so its growth moves
            * nothing, and a ten-row reserve would leave a hole under a client with two records.
            * Measured cold before the change, the one-line notice grew 214.75px in a frame. */}
          {gate.waiting || view.feed.length === 0 ? (
            /* The wait and the empty answer stand in ONE box, three rows tall — see `.rowsWait3`.
             * Without it the skeleton's own arrival was a 152px jump, which is the defect it was
             * added to fix, one step further in.
             *
             * Both halves of the fork are the GATE's (21 September 2026): on the read, the rows
             * replaced the bars the instant `/records` answered and then blinked when the class
             * arrived 300ms later. `aria-busy` goes with them, on the owner's correction the same
             * day: while the bars are still standing this box is still about to change, and saying
             * it is settled the moment `/records` answered is a settlement claimed before it
             * happened. Branch, bars and hidden sentence all key on `gate.waiting`. */
            <div className={styles.rowsWait3} aria-busy={gate.waiting}>
              {gate.waiting ? (
                <>
                  <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
                  {gate.skeleton ? (
                    <ul className={styles.prList}>
                      {[0, 1, 2].map((slot) => (
                        <li key={slot} className={styles.prRow}>
                          <i className={`${String(skel.bar)} ${String(styles.skelPrMark)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrKind)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrName)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrValue)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrWhen)}`} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                /* NEUTRAL: a client with no personal records yet is a fact about a window of
                 * training, and there is nothing here for the trainer to press. */
                <EmptyState
                  mark={<TrendMarkIcon />}
                  tone="neutral"
                  title={t('clients.detail.records.empty')}
                />
              )}
            </div>
          ) : (
            <ul className={`${String(styles.prList)} ${gate.reveal}`}>
              {/* One row per SET, with an axis for each record it held.
                *
                * The wire emits one entry per (set, kind) pair and says so — "a new heaviest weight
                * is usually also a new e1RM" — so the feed used to stack the same exercise on the
                * same date two or three times, reading as separate achievements. At ten entries
                * that is three or four real events printed as ten. */}
              {view.feed.map((row) => {
                const leading = row.axes[0];
                return (
                  <li key={row.key} className={styles.prRow}>
                    {/* The mark takes the LEADING axis — `axes` is ordered by `PR_KIND_ORDER`, so on
                      * a set that broke two records at once it is the heavier fact. Every axis is
                      * still printed in words beside it. */}
                    <LeadingAxisMark kind={leading?.kind ?? 'e1rm'} />
                    <em className={styles.prKind}>{leading?.kindLabel ?? ''}</em>
                    <b className={styles.prName}>{row.exerciseName}</b>
                    <span className={styles.prValue}>{leading?.value ?? ''}</span>
                    <b className={styles.prGain}>{leading?.improvement ?? ''}</b>
                    <em className={styles.prWhen}>{row.whenLabel}</em>
                    {/* A set that broke a second or third record says so here rather than in the
                      * row's fixed columns, which hold one value each. Never dropped: a row that
                      * broke three records must not print only the largest. */}
                    {row.axes.length < 2 ? null : (
                      <span className={styles.prExtra}>
                        {row.axes.slice(1).map((axis) => (
                          <span key={axis.kind} className={styles.prExtraItem}>
                            <em className={styles.prKind}>{axis.kindLabel}</em>
                            {axis.value}
                            {axis.improvement === null ? null : (
                              <b className={styles.prGain}>{axis.improvement}</b>
                            )}
                          </span>
                        ))}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={styles.card}>
          <div className={styles.cardHead}>
            <em className={eyebrows.eyebrow}>{t('web:clientDetail.records.perExerciseEyebrow')}</em>
          </div>

          {gate.waiting || view.perExercise.length === 0 ? (
            <div className={styles.tableWait3} aria-busy={gate.waiting}>
              {gate.waiting ? (
                <>
                  <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
                  {gate.skeleton ? (
                    <ul className={styles.prList}>
                      {[0, 1, 2].map((slot) => (
                        <li key={slot} className={styles.skelTableRow}>
                          <i className={`${String(skel.bar)} ${String(styles.skelPrName)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrValue)}`} />
                          <i className={`${String(skel.bar)} ${String(styles.skelPrWhen)}`} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <EmptyState
                  mark={<TrendMarkIcon />}
                  tone="neutral"
                  title={t('clients.detail.records.empty')}
                />
              )}
            </div>
          ) : (
            <div className={`${String(styles.tableScroll)} ${gate.reveal}`}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col" className={styles.th}>
                      {t('web:clientDetail.records.columnExercise')}
                    </th>
                    <th scope="col" className={`${String(styles.th)} ${String(styles.thNumeric)}`}>
                      {t('web:clientDetail.records.columnBest')}
                    </th>
                    <th scope="col" className={`${String(styles.th)} ${String(styles.thNumeric)}`}>
                      {t('web:clientDetail.records.columnE1rm')}
                    </th>
                    <th scope="col" className={`${String(styles.th)} ${String(styles.thWhen)}`}>
                      {t('web:clientDetail.records.columnWhen')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {view.perExercise.map((row) => (
                    <tr key={row.key}>
                      <th scope="row" className={styles.tdName}>
                        {row.exerciseName}
                      </th>
                      <td className={styles.tdNumeric}>{row.best ?? '—'}</td>
                      <td className={`${String(styles.tdNumeric)} ${String(styles.tdE1rm)}`}>
                        {row.bestE1rm ?? '—'}
                      </td>
                      <td className={styles.tdWhen}>{row.whenLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The 30px mark at the head of a feed row: v3's solid lime square with the axis's glyph in dark
 * ink on it.
 *
 * A component rather than three lines inline, because the two parts of "what this axis looks like"
 * have to move together — a tile without its glyph is the bare square the owner rejected on
 * 12 August 2026.
 */
function LeadingAxisMark({ kind }: { kind: PrKind }) {
  const Glyph = AXIS_GLYPH[kind];
  return (
    <span className={styles.prMark} aria-hidden="true">
      <Glyph className={styles.prGlyph} />
    </span>
  );
}
